// レイヤー描画状態の購読と、レイヤー操作（追加・削除・表示切替・ズーム・エクスポート・undo/redo）。
//
// 役割: App.jsx から切り出したレイヤー結線フック。LayerStore の単一インスタンスを所有し、
//       購読スナップショット（layers）と各操作ハンドラを返す。DuckDB 本体の操作は
//       data/layer-service へ委譲し、地図ビュー更新は注入された fitToBounds に任せる。
// 注入: { fitToBounds, log }（どちらも App が握る地図ビュー/ログの共有点）。
import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react'

import { LayerStore } from '../data/layer-store'
import { deleteLayer, recreateLayer, exportLayer, describeLayer } from '../data/layer-service'
import { downloadFile } from '../utils/download'
import {
  loadSessionTables,
  removeSessionTable,
  restoreSessionTable,
  saveSessionTable,
} from '../data/session-storage'
import { registerLayer } from '../duckdb/layer-registry'
import { recolorChoroplethStyle } from '../viz/choropleth'

// モジュールスコープの単一インスタンス（描画状態の単一の真実）。
const layerStore = new LayerStore()

export function useLayerActions({ fitToBounds, log, ready = false }) {
  // --- レイヤー描画状態（ストアを購読） ---
  const layers = useSyncExternalStore(layerStore.subscribe, layerStore.getSnapshot)
  const restoredRef = useRef(false)

  useEffect(() => {
    if (!ready || restoredRef.current) return
    restoredRef.current = true
    let cancelled = false
    async function restore() {
      const records = await loadSessionTables('layer')
      for (const record of records) {
        if (cancelled) return
        try {
          await restoreSessionTable(record)
          const layer = record.meta
          await registerLayer({
            layerId: layer.layerId,
            displayName: layer.displayName,
            geomType: layer.geomType,
            featureCount: layer.featureCount,
            sourceLayers: layer.sourceLayers,
            originPrompt: layer.originPrompt,
            originSql: layer.originSql,
          })
          layerStore.add(layer)
        } catch (e) {
          log(`レイヤー復元エラー: ${record.id}: ${String(e?.message ?? e)}`)
        }
      }
    }
    restore()
    return () => {
      cancelled = true
    }
  }, [ready, log])

  // レイヤーを地図へ反映する（新規は追加+フィット、既存は upsert で更新）。
  // 色が未割当なら割り当てる。手動取込・SQL・エージェントの全経路で共通利用する。
  const addLayerToMap = useCallback(
    (layer) => {
      const colored = layer.color ? layer : { ...layer, color: layerStore.nextColor() }
      if (layerStore.get(layer.layerId)) {
        layerStore.update(layer.layerId, { ...colored, visible: true })
      } else {
        layerStore.add(colored)
        fitToBounds(colored.bounds)
      }
      void saveSessionTable('layer', colored.layerId, colored)
    },
    [fitToBounds],
  )

  // レイヤーを地図（ストア）から取り除く。
  const dropLayerFromMap = useCallback((layerId) => {
    const removed = layerStore.remove(layerId)
    void removeSessionTable('layer', layerId)
    return removed
  }, [])

  // レイヤーの描画スタイル（コロプレス等）を差し替える。null で単色に戻す。
  const setLayerStyle = useCallback((layerId, style) => {
    layerStore.update(layerId, { style })
    const layer = layerStore.get(layerId)
    if (layer) void saveSessionTable('layer', layerId, layer)
  }, [])

  const handleAppearanceChange = useCallback((layerId, change) => {
    const layer = layerStore.get(layerId)
    if (!layer) return
    const patch = {}
    if (change.fillColor) patch.fillColor = change.fillColor
    if (change.strokeColor) patch.strokeColor = change.strokeColor
    if (change.scheme && layer.style?.type === 'choropleth') {
      patch.style = recolorChoroplethStyle(layer.style, change.scheme)
    }
    layerStore.update(layerId, patch)
    const updated = layerStore.get(layerId)
    if (updated) void saveSessionTable('layer', layerId, updated)
  }, [])

  // レイヤー操作。
  const handleToggle = useCallback((id) => {
    layerStore.toggle(id)
    const layer = layerStore.get(id)
    if (layer) void saveSessionTable('layer', id, layer)
  }, [])
  const handleZoom = useCallback(
    (id) => {
      const layer = layerStore.get(id)
      if (layer) fitToBounds(layer.bounds)
    },
    [fitToBounds],
  )

  const handleRemove = useCallback(
    async (id) => {
      const removed = layerStore.remove(id)
      if (!removed) return
      try {
        await deleteLayer(id)
        await removeSessionTable('layer', id)
        log(`削除: ${removed.displayName}`)
      } catch (e) {
        log(`削除エラー: ${String(e?.message ?? e)}`)
      }
    },
    [log],
  )

  // レイヤーを指定形式でダウンロード（GeoJSON / GeoParquet）。
  const handleExport = useCallback(
    async (layerId, format) => {
      try {
        const { filename, mime, data } = await exportLayer(layerId, format)
        downloadFile(filename, data, mime)
        log(`エクスポート: ${filename}`)
      } catch (e) {
        log(`エクスポートエラー: ${String(e?.message ?? e)}`)
      }
    },
    [log],
  )

  // レイヤー詳細モーダル用の属性プレビュー（列 + 先頭 10 行）を取得する。
  const handleDescribe = useCallback((id) => describeLayer(id, { sampleRows: 10 }), [])

  // undo/redo: ストアが返す副作用記述子に従って DuckDB を操作する（plan.md §6.3）。
  const applyEffect = useCallback(
    async (eff) => {
      if (!eff) return
      try {
        if (eff.effect === 'drop') {
          await deleteLayer(eff.layer.layerId)
          await removeSessionTable('layer', eff.layer.layerId)
          log(`元に戻す: ${eff.layer.displayName} を削除`)
        } else if (eff.effect === 'recreate') {
          await recreateLayer(eff.layer)
          await saveSessionTable('layer', eff.layer.layerId, eff.layer)
          fitToBounds(eff.layer.bounds)
          log(`復元: ${eff.layer.displayName}`)
        }
      } catch (e) {
        log(`履歴操作エラー: ${String(e?.message ?? e)}`)
      }
    },
    [fitToBounds, log],
  )

  const handleUndo = useCallback(() => applyEffect(layerStore.undo()), [applyEffect])
  const handleRedo = useCallback(() => applyEffect(layerStore.redo()), [applyEffect])

  const clearLayers = useCallback(async () => {
    const current = layerStore.getSnapshot()
    await Promise.all(current.map((layer) => deleteLayer(layer.layerId).catch(() => {})))
    layerStore.clear()
  }, [])

  return {
    layers,
    addLayerToMap,
    dropLayerFromMap,
    setLayerStyle,
    handleToggle,
    handleZoom,
    handleRemove,
    handleExport,
    handleDescribe,
    handleAppearanceChange,
    handleUndo,
    handleRedo,
    clearLayers,
    // 履歴の可否はレンダー時に算出（layers 変化のたび再評価される）。
    canUndo: layerStore.canUndo(),
    canRedo: layerStore.canRedo(),
  }
}
